import { useEffect, useState } from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";

const BRAND = window.BRAND;

// Every buyer's own Firebase project ID doubles as their CUSTOMER_ID for
// licensing purposes — already globally unique, already known the moment
// their project exists, so there's no separate per-buyer ID to configure
// in brand-config.js. Reads the *default* app (src/firebase.js), not the
// secondary "license" app below.
export function getCustomerId() {
  return getApp().options.projectId;
}

// Both paid products (core portal + Job Estimator) are checked against the
// seller's shared license service (a separate Firebase project reused by
// every deployed buyer instance — see /senate-license-service). This opens
// a second, distinctly-named Firebase app so it can read that project's
// Firestore without touching this instance's own `db`/`storage` from
// src/firebase.js.
export function getLicenseDb() {
  const name = "license";
  const app = getApps().some((a) => a.name === name)
    ? getApp(name)
    : initializeApp(BRAND.LICENSE_FIREBASE_CONFIG, name);
  return getFirestore(app);
}

// Fire-and-forget: tells the license service the buyer's current team-member
// count (direct staff) and contractor crew count so it can keep both Stripe
// subscription items in sync — staff on the Core Portal price, contractor
// crew on the separate half-price Contractor Crew Seats item.
export function syncSeatCount(seatCount, contractorSeatCount = 0) {
  fetch(`${BRAND.LICENSE_API_BASE}/syncSeatCount`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerId: getCustomerId(), seatCount, contractorSeatCount }),
  }).catch(() => {});
}

// Fire-and-forget: reports an uncaught client-side error to a central log
// (see window.onerror/onunhandledrejection wiring in App.jsx). Never throws
// itself — an error-reporting call failing shouldn't cascade into more errors.
export function reportError(message, stack) {
  try {
    fetch(`${BRAND.LICENSE_API_BASE}/logClientError`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerId: getCustomerId(), message, stack, url: window.location.href }),
    }).catch(() => {});
  } catch {
    // getCustomerId() can throw before initFirebase() resolves — ignore
  }
}

// Creates or updates this buyer's real admin account (see setAdminAccount
// Cloud Function in senate-license-service). Throws with a readable message
// on failure so the caller (AdminSetup's form) can show it directly.
export async function setAdminAccount(email, pin) {
  const res = await fetch(`${BRAND.LICENSE_API_BASE}/setAdminAccount`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerId: getCustomerId(), email, pin }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to save admin account");
  return data;
}

// Verifies a Team Member's PIN against the license service's own
// teamMemberLogin Cloud Function instead of this app ever downloading the
// full teamMembers roster (names/photos/PINs) to the browser to check
// locally. Throws with a readable message on a wrong PIN, same shape the
// callers already expect from the old client-side check.
export async function teamMemberLogin(pin) {
  const res = await fetch(`${BRAND.LICENSE_API_BASE}/teamMemberLogin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerId: getCustomerId(), pin }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Invalid PIN");
  return data.member;
}

// Upgrades whichever add-on subscription is already active onto the
// bundle price ($25/mo for Estimator + Supply Tracker together, see
// upgradeToBundle Cloud Function) instead of starting a new one.
export async function upgradeToBundle() {
  const res = await fetch(`${BRAND.LICENSE_API_BASE}/upgradeToBundle`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerId: getCustomerId() }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to upgrade to bundle");
  return data;
}

// Sends a Supply Tracker restock request (see sendSupplyRequestEmail Cloud
// Function in senate-license-service) — a plain HTTP call, not tied to
// customerId, since it just needs the client's own configured emails.
export async function sendSupplyRequest({ toEmails, clientName, items, notes, photoUrl, requestedBy }) {
  const res = await fetch(`${BRAND.LICENSE_API_BASE}/sendSupplyRequestEmail`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ toEmails, clientName, items, notes, photoUrl, requestedBy }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to send supply request");
  return data;
}

// Emails a signed Work Order PDF as an attachment (see sendWorkOrderEmail
// Cloud Function in senate-license-service, which holds the shared Resend
// secret). pdfBase64 is raw base64 (no data: prefix) — callers get this
// from jsPDF's doc.output("datauristring") split on "base64,".
export async function sendWorkOrderEmail(to, title, pdfBase64) {
  const res = await fetch(`${BRAND.LICENSE_API_BASE}/sendWorkOrderEmail`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerId: getCustomerId(), to, title, pdfBase64 }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to email work order");
  return data;
}

// Texts a link to a signed Work Order PDF (already uploaded to this
// instance's own Storage by the caller) — reuses the existing sendSmsAlert
// endpoint (same one behind clock-in/out and missed-visit alerts) rather
// than a dedicated one, since a plain {to, body} text is all it needs.
export async function sendWorkOrderText(to, body) {
  const res = await fetch(`${BRAND.LICENSE_API_BASE}/sendSmsAlert`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ customerId: getCustomerId(), to: [to], body }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to text work order");
  return data;
}

// Calls a license-service endpoint (createCheckoutSession / createPortalSession)
// and redirects the browser to the returned Stripe URL. Returns false (without
// redirecting) on any failure, so callers can reset their own busy/loading state.
export async function redirectToLicenseEndpoint(endpoint, body) {
  try {
    const res = await fetch(`${BRAND.LICENSE_API_BASE}/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerId: getCustomerId(), ...body }),
    });
    const { url } = await res.json();
    if (url) {
      window.location.href = url;
      return true;
    }
  } catch {
    // fall through to false
  }
  return false;
}

// Shared paywall check for any add-on gated the same way as the Job
// Estimator (see EstimatorGate.jsx) — watches licenses/{customerId}.{product}
// in the license service's own Firestore. Fails safe to "inactive" if the
// listener never resolves (e.g. LICENSE_FIREBASE_CONFIG misconfigured).
export function useLicenseStatus(product) {
  const [status, setStatus] = useState("loading"); // loading | active | inactive
  useEffect(() => {
    const timeout = setTimeout(() => setStatus((s) => (s === "loading" ? "inactive" : s)), 6000);
    const licenseDb = getLicenseDb();
    const unsub = onSnapshot(
      doc(licenseDb, "licenses", getCustomerId()),
      (snap) => {
        clearTimeout(timeout);
        const data = snap.exists() ? snap.data() : null;
        setStatus(data?.[product]?.status === "active" ? "active" : "inactive");
      },
      () => { clearTimeout(timeout); setStatus("inactive"); }
    );
    return () => { clearTimeout(timeout); unsub(); };
  }, [product]);
  return status;
}
